import React, { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ChevronDownIcon from 'images/v5/icons/chevron-down.svg';
import ChevronUpIcon from 'images/v5/icons/chevron-up.svg';
import PencilIcon from 'images/v5/icons/edit-02.svg';
import Button from 'layout/atoms/Button/Button';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import Loader from 'layout/atoms/Loader/Loader';
import { Cell, Row } from 'layout/atoms/TableGrid';
import EmptyState from 'layout/molecules/EmptyState/EmptyState';
import Grid from 'molecules/Grid/Grid';
import { openModal } from 'store/layoutSlice';
import { fetchProductCategories, moveProductCategory, selectProductCategories, selectProductCategoriesErrors, selectProductCategoriesRefetchFlags, selectProductCategoriesStatus, } from 'store/parameters/productCategoriesSlice';
import getCategoryIcon from 'utils/getCategoryIcon';
import ParametersTable from '../shared/ParametersTable';
import styles from './ProductCategories.module.scss';
function ProductCategories() {
    const dispatch = useDispatch();
    const { t } = useTranslation(['specification', 'common']);
    const productCategories = useSelector(selectProductCategories);
    const status = useSelector(selectProductCategoriesStatus);
    const errors = useSelector(selectProductCategoriesErrors);
    const refetchFlags = useSelector(selectProductCategoriesRefetchFlags);
    const isMoving = status.move === 'loading';
    useEffect(() => {
        if (status.fetch === 'idle' || refetchFlags.fetch) {
            dispatch(fetchProductCategories());
        }
    }, [dispatch, status.fetch, refetchFlags.fetch]);
    const handleMove = (productCategoryId, direction) => {
        dispatch(moveProductCategory({ productCategoryId, direction }));
    };
    if (status.fetch === 'loading' || status.fetch === 'idle') {
        return React.createElement(Loader, null);
    }
    if (status.fetch === 'failed') {
        return React.createElement("div", null, errors.fetch);
    }
    if (productCategories.length === 0) {
        return React.createElement(EmptyState, null);
    }
    return (React.createElement(Grid, { gap: 24 },
        React.createElement(ParametersTable, { columns: [
                ['120px', '120px'],
                ['240px', '1fr'],
                ['240px', '1fr'],
                ['120px', '140px'],
                ['120px', '160px'],
                ['72px', '72px'],
            ], headers: [
                { content: t('display_order') },
                { content: t('product_category_name_pl') },
                { content: t('product_category_name_en') },
                { content: t('product_category_filters') },
                { content: t('components_count_header') },
                { content: null },
            ] }, productCategories.map((productCategory, index) => {
            var _a;
            const Icon = getCategoryIcon(productCategory.typeIcon);
            const isFirst = index === 0;
            const isLast = index === productCategories.length - 1;
            return (React.createElement(Row, { key: productCategory.id },
                React.createElement(Cell, null,
                    React.createElement("div", { className: styles.orderActions },
                        React.createElement("span", { className: styles.orderNumber }, index + 1),
                        React.createElement(Button, { size: "xs", shape: "squared", theme: "ghost", title: t('move_category_up'), disabled: isFirst || isMoving, leftIcon: React.createElement(ChevronUpIcon, null), onClick: () => handleMove(productCategory.id, 'UP') }),
                        React.createElement(Button, { size: "xs", shape: "squared", theme: "ghost", title: t('move_category_down'), disabled: isLast || isMoving, leftIcon: React.createElement(ChevronDownIcon, null), onClick: () => handleMove(productCategory.id, 'DOWN') }))),
                React.createElement(Cell, null,
                    React.createElement("div", { className: styles.categoryLabel },
                        React.createElement("span", { className: styles.categoryIcon },
                            React.createElement(Icon, null)),
                        React.createElement("p", null, productCategory.typeLabelPL))),
                React.createElement(Cell, null, productCategory.typeLabelEN),
                React.createElement(Cell, null, productCategory.filtersAvailable ? t('common:yes') : t('common:no')),
                React.createElement(Cell, null, (_a = productCategory.productsCount) !== null && _a !== void 0 ? _a : 0),
                React.createElement(Cell, null,
                    React.createElement(Dropdown, null,
                        React.createElement(Button, { size: "m", align: "left", theme: "ghost", leftIcon: React.createElement(PencilIcon, null), onClick: () => dispatch(openModal({ name: 'productCategoryEditor', id: productCategory.id })) }, t('common:edit'))))));
        }))));
}
export default memo(ProductCategories);
